import React, { useState, useEffect } from "react";
import { Menu, X, ChevronDown } from "lucide-react";
import { Button } from "./ui/button";

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { name: "About", href: "#about" },
    { name: "Our Companies", href: "#divisions" },
    { name: "Ghost™ Assistant", href: "#ghost" },
    { name: "Impact", href: "#impact" },
  ];

  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
    setIsMobileMenuOpen(false);
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? "glass shadow-sm" : "bg-transparent"
      }`}
      data-testid="header"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <a
            href="/"
            className="flex items-center gap-3"
            data-testid="header-logo"
          >
            <div className="w-10 h-10 bg-[#0A192F] rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-xl font-['Outfit']">P</span>
            </div>
            <span className="text-xl font-semibold text-[#0A192F] font-['Outfit'] hidden sm:block">
              Prestigious Companies
            </span>
          </a>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-8" data-testid="desktop-nav">
            {navLinks.map((link) => (
              <button
                key={link.name}
                onClick={() => scrollToSection(link.href)}
                className="nav-link text-sm font-medium"
                data-testid={`nav-link-${link.name.toLowerCase().replace(" ", "-")}`}
              >
                {link.name}
              </button>
            ))}
          </nav>

          {/* CTA Buttons */}
          <div className="hidden lg:flex items-center gap-4">
            <Button
              onClick={() => scrollToSection("#contact")}
              className="bg-[#0A192F] hover:bg-[#112240] text-white px-6"
              data-testid="contact-cta"
            >
              Get in Touch
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            data-testid="mobile-menu-button"
          >
            {isMobileMenuOpen ? (
              <X className="w-6 h-6 text-[#0A192F]" />
            ) : (
              <Menu className="w-6 h-6 text-[#0A192F]" />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/20 z-30"
          onClick={() => setIsMobileMenuOpen(false)}
          data-testid="mobile-menu-overlay"
        />
      )}
      <div
        className={`lg:hidden fixed top-0 right-0 bottom-0 w-full max-w-sm bg-white z-40 pt-24 px-6 transition-transform duration-300 shadow-xl ${
          isMobileMenuOpen ? "translate-x-0" : "translate-x-full"
        }`}
        data-testid="mobile-menu"
      >
        {/* Close button inside mobile menu */}
        <button
          className="absolute top-6 right-6 p-2"
          onClick={() => setIsMobileMenuOpen(false)}
          data-testid="mobile-menu-close"
        >
          <X className="w-6 h-6 text-[#0A192F]" />
        </button>
        <nav className="flex flex-col gap-6">
          {navLinks.map((link) => (
            <button
              key={link.name}
              onClick={() => scrollToSection(link.href)}
              className="text-lg font-medium text-[#0A192F] text-left py-2 border-b border-slate-100"
              data-testid={`mobile-nav-link-${link.name.toLowerCase().replace(" ", "-")}`}
            >
              {link.name}
            </button>
          ))}
          <div className="pt-6 flex flex-col gap-4">
            <button
              onClick={() => scrollToSection("#contact")}
              className="btn-primary"
              data-testid="mobile-contact-cta"
            >
              Get in Touch
            </button>
          </div>
        </nav>
      </div>
    </header>
  );
};

export default Header;
